import {
  BlockFragment,
  SectionFragment,
} from '__generated__/queries-newsletter';
import { BlockItemType } from 'pages/CampaignPage/CampaignEditPage/ContentPage/Items/utils';
import {
  compose,
  findIndex,
  insert,
  lensPath,
  move,
  over,
  propEq,
  remove,
  view,
} from 'ramda';
import { DropResult } from 'react-beautiful-dnd';
import { EnrichedCampaignSection } from '../Sections';
import { CreateBlocks, emptyBlock } from './emptyTypes';

const updatePositions = (array: any[]) =>
  array.map((item, i: number) => ({ ...item, position: i }));

export const manageDrag = (
  result: DropResult,
  articles: BlockItemType[],
  sections: EnrichedCampaignSection[],
): SectionFragment[] => {
  if (!result.destination) {
    return sections;
  }
  const { source } = result;
  const { destination } = result;

  // case when we drop an article inside a section => add block to the section
  if (
    source.droppableId === 'article' &&
    destination.droppableId.includes('subjectSection')
  ) {
    const [, sectionId] = destination.droppableId.split('_');
    const sectionIndex: number = findIndex(propEq('id', sectionId))(sections);
    const articleSource = articles[source.index];
    const newBlock = emptyBlock({
      url: articleSource.url,
      media: {
        focusPoint: articleSource.media?.focusPoint,
        iframely: articleSource.media?.iframely,
      },
      blockType: articleSource.blockType || '',
      position: destination.index,
      title: articleSource.title as string,
      content:
        articleSource.content && typeof articleSource.content !== 'string'
          ? articleSource.content.getCurrentContent().getPlainText()
          : articleSource.content,
    });
    const lens = lensPath([sectionIndex, 'blocks']);
    const reorderedBlocks = over(
      lens,
      (blocks: CreateBlocks[]) =>
        compose(updatePositions, insert(destination.index, newBlock))(blocks),
      sections,
    );

    return reorderedBlocks;
  }
  // case when we move sections
  if (
    source.droppableId === 'sections' &&
    destination.droppableId === 'sections'
  ) {
    const reorderedSections = (compose(
      updatePositions,
      move(source.index, destination.index) as any,
    ) as any)(sections);

    return reorderedSections;
  }
  // case when we move blocks inside a section or between sections
  if (
    source.droppableId.includes('subjectSection') &&
    destination.droppableId.includes('subjectSection')
  ) {
    const [, sourceSectionId] = source.droppableId.split('_');
    const [, destinationSectionId] = destination.droppableId.split('_');
    if (sourceSectionId === destinationSectionId) {
      const sectionIndex: number = findIndex(propEq('id', sourceSectionId))(
        sections,
      );
      const lens = lensPath([sectionIndex, 'blocks']);
      const reorderedBlocks = over(
        lens,
        (blocks: BlockFragment[]) =>
          (compose(
            updatePositions,
            move(source.index, destination.index) as any,
          ) as any)(blocks),
        sections,
      );
      return reorderedBlocks;
    }
    const sourceSectionIndex = findIndex(propEq('id', sourceSectionId))(
      sections,
    );
    const destinationIndex = findIndex(propEq('id', destinationSectionId))(
      sections,
    );

    const lens = lensPath([sourceSectionIndex, 'blocks']);
    const block: BlockFragment = view<SectionFragment[], BlockFragment[]>(
      lens,
      sections,
    )[source.index];

    const removedfromSection = over(
      lens,
      (blocks: BlockFragment[]) =>
        compose(updatePositions, remove(source.index, 1))(blocks),
      sections,
    );
    const lensDestination = lensPath([destinationIndex, 'blocks']);
    const finalSections = over(
      lensDestination,
      (blocks: CreateBlocks[]) =>
        compose(
          updatePositions,
          insert(destination.index, { ...block, isCreate: true }),
        )(blocks),
      removedfromSection,
    );

    return finalSections;
  }

  return sections;
};
